import React, { useState, useMemo } from 'react';
import { Crown, Users, Check, AlertTriangle, ArrowRight, Loader2, X } from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';
import api from '../../api';

const CURRENCY_CONFIG = {
  zh: { symbol: '¥', rate: 1, name: '人民币' },
  en: { symbol: '$', rate: 0.14, name: 'USD' },
  ja: { symbol: '¥', rate: 14, name: '日本円' },
  ko: { symbol: '₩', rate: 190, name: '원' },
  es: { symbol: '€', rate: 0.12, name: 'EUR' }
};

const BASE_PRICE = 99;
const MONTHS_PER_YEAR = 11;

const SubscriptionModal = ({ isOpen, onClose, t, msgCount, language = 'zh', userId }) => {
  const [plan, setPlan] = useState('yearly');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const { monthlyPrice, yearlyPrice, currency } = useMemo(() => {
    const config = CURRENCY_CONFIG[language] || CURRENCY_CONFIG.en;
    const monthly = BASE_PRICE * config.rate;
    const yearly = monthly * MONTHS_PER_YEAR;

    const formatPrice = (price) => {
      if (language === 'ja' || language === 'ko') {
        return Math.round(price).toLocaleString('ja-JP');
      }
      return Math.round(price).toLocaleString('en-US');
    };

    return {
      monthlyPrice: formatPrice(monthly),
      yearlyPrice: formatPrice(yearly),
      currency: config.symbol
    };
  }, [language]);

  if (!isOpen) return null;

  const handleSubscribe = async () => {
    triggerHaptic();
    setProcessing(true);
    setError(null);

    try {
      const result = await api.createSubscriptionOrder(userId, plan);

      if (result.simulated) {
        alert(result.message);
        onClose();
        window.location.reload();
        return;
      }

      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.response?.data?.error || '订阅失败，请稍后重试');
    } finally {
      setProcessing(false);
    }
  };

  const remaining = Math.max(0, 100 - msgCount);

  return (
    <div className="absolute inset-0 z-[90] bg-[#050510]/95 backdrop-blur-3xl flex flex-col page-enter">
      <div className="safe-top pt-6 px-6 flex justify-between items-center mb-6">
        <div>
           <h2 className="text-3xl font-black text-[var(--neon-warning)] flex items-center gap-3 text-glow"><Crown size={28} fill="currentColor" /> {t.neuralPass}</h2>
           <p className="text-xs text-gray-400 font-mono mt-1 tracking-wider">{t.unlockPot}</p>
        </div>
        <button onClick={onClose} className="p-3 bg-white/10 rounded-full hover:bg-white/20"><X size={24} className="text-white" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-6 safe-bottom">
        <div className="grid grid-cols-2 gap-5 mb-8">
          <div className="p-6 rounded-[32px] glass-card flex flex-col items-center text-center opacity-70 grayscale">
            <span className="text-[10px] font-mono mb-4 text-gray-400 uppercase tracking-widest">{t.basic}</span>
            <div className="w-14 h-14 bg-white/5 rounded-full flex items-center justify-center mb-4"><Users size={24} className="text-gray-400"/></div>
            <ul className="space-y-3 text-xs text-gray-300 font-medium">
              <li>{t.maxGroup}</li>
              <li>{t.msgLimit}</li>
            </ul>
          </div>

          <div className="p-6 rounded-[32px] glass-card border-[var(--neon-warning)]/50 flex flex-col items-center text-center relative overflow-hidden shadow-[0_0_40px_rgba(244,215,59,0.15)] transform scale-105 z-10">
            <div className="absolute top-0 right-0 bg-[var(--neon-warning)] text-black text-[10px] font-black px-3 py-1.5 rounded-bl-2xl">PRO</div>
            <span className="text-[10px] font-mono mb-4 text-[var(--neon-warning)] uppercase tracking-widest">{t.matrixGold}</span>
            <div className="w-14 h-14 bg-[var(--neon-warning)]/20 rounded-full flex items-center justify-center mb-4"><Crown size={24} className="text-[var(--neon-warning)]"/></div>
            <ul className="space-y-3 text-xs text-white font-bold">
              <li className="flex items-center gap-2 justify-center"><Check size={14}/> {t.unlimitedGroups}</li>
              <li className="flex items-center gap-2 justify-center"><Check size={14}/> {t.infiniteChat}</li>
            </ul>
          </div>
        </div>

        <div className="mb-8 p-5 rounded-3xl bg-red-500/10 border border-red-500/30 flex items-start gap-4 backdrop-blur-md">
          <div className="p-2.5 bg-red-500/20 rounded-xl"><AlertTriangle size={24} className="text-red-500" /></div>
          <div>
            <p className="text-xs text-red-200 font-bold mb-1 uppercase tracking-wide">{t.sysWarning}</p>
            <p className="text-sm text-white">{t.cyclesRemaining} <span className="font-bold text-red-400 text-lg ml-1">{remaining}/100</span></p>
          </div>
        </div>

        <div className="flex bg-black/40 p-2 rounded-[24px] mb-8 border border-white/10 backdrop-blur-xl">
          <button onClick={() => setPlan('monthly')} className={`flex-1 py-4 rounded-2xl text-sm font-bold transition-all ${plan === 'monthly' ? 'bg-white text-black shadow-lg' : 'text-gray-500 hover:text-white'}`}>{t.monthly}</button>
          <button onClick={() => setPlan('yearly')} className={`flex-1 py-4 rounded-2xl text-sm font-bold transition-all relative ${plan === 'yearly' ? 'bg-[var(--neon-warning)] text-black shadow-lg' : 'text-gray-500 hover:text-white'}`}>
            {t.yearly}
            <span className="absolute -top-3 -right-3 bg-red-600 text-white text-[9px] px-2.5 py-1 rounded-full border border-black font-black">{t.saveOff}</span>
          </button>
        </div>

        <div className="text-center mb-10">
          <div className="flex items-end justify-center gap-1 mb-1">
            <span className="text-6xl font-black text-white tracking-tighter text-glow">{plan === 'monthly' ? `${currency}${monthlyPrice}` : `${currency}${yearlyPrice}`}</span>
            <span className="text-base text-gray-400 mb-3 font-bold">/{plan === 'monthly' ? 'mo' : 'yr'}</span>
          </div>
          {plan === 'yearly' && <p className="text-xs text-[var(--neon-warning)] font-bold tracking-wide">{t.pay11get12}</p>}
        </div>

        <button onClick={handleSubscribe} disabled={processing} className="w-full py-5 bg-gradient-to-r from-[var(--neon-warning)] to-orange-600 text-black font-black text-lg rounded-[28px] shadow-[0_0_40px_rgba(244,215,59,0.4)] hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 group relative overflow-hidden">
          {processing ? <Loader2 className="animate-spin" /> : <><ArrowRight size={24} /> {t.establishUplink}</>}
          <div className="absolute top-0 -left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent transform skew-x-12 group-hover:animate-shine transition-all duration-1000"></div>
        </button>

        {error && (
          <div className="mt-4 p-4 rounded-2xl bg-red-500/20 border border-red-500/40 text-red-300 text-sm text-center">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionModal;
